import React, { useEffect, useLayoutEffect, useRef, useState, RefObject } from 'react';
import { useMDCockpit } from '../../../hooks/useMDCockpit';
import { useCesium } from '../../../context/CesiumContext';
import { useMission } from '../../../context/MissionContext';
import { showToastMessage } from '../../../../../../utils/commonFunctions';
import styles from './LayersPanel.module.scss';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';

// ─── Icons ────────────────────────────────────────────────────────────────────
const LayersIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="m12 2 10 6-10 6L2 8z" />
    <path d="m2 14 10 6 10-6" />
  </svg>
);
const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const CheckIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// ─── Layer data ───────────────────────────────────────────────────────────────
const OBJECT_LAYERS = [
  { key: 'satellites', label: 'Satellite' },
  { key: 'geofeatures', label: 'GeoFeatures' },
  { key: 'ground', label: 'Ground Stations' },
];

const VIZ_LAYERS = [
  { key: 'trails', label: 'Orbital Trails' },
  { key: 'groundTracks', label: 'Ground Tracks' },
  { key: 'sensorFovs', label: 'Sensor FOVs' },
  { key: 'gsFov', label: 'GS FOV' },
  { key: 'los', label: 'LOS' },
  { key: 'labels', label: 'Object Labels' },
];

const MAP_STYLE_OPTIONS = [
  { value: 'dark', label: 'Dark', tone: 'dark' },
  { value: 'light', label: 'Light', tone: 'light' },
  { value: 'streets', label: 'Streets', tone: 'streets' },
  { value: 'satellite', label: 'Satellite', tone: 'satellite' },
  { value: 'outdoors', label: 'Outdoors', tone: 'outdoors' },
] as const;

// ─── Props ────────────────────────────────────────────────────────────────────
interface Props {
  btnRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────
const LayersPanel: React.FC<Props> = ({ btnRef, onClose }) => {
  const {
    layers, toggleLayer, viewMode, setViewMode, timelineCollapsed, timelineHeight,
    groundStations = [], geoFeatures = [],
  } = useMDCockpit();
  const { state } = useMission();
  const satellites = state?.satellites || [];
  const cesium = useCesium();
  const panelRef = useRef<HTMLDivElement>(null);

  const hasSats = satellites.length > 0;
  const hasGS = groundStations.length > 0;
  const hasGeo = geoFeatures.length > 0;
  const hasAnyObject = hasSats || hasGS || hasGeo;

  const hasAnySensor = satellites.some((item: any) => {
    if (item.type === 'group') {
      return (item.children || []).some((child: any) => (child.sensors || []).length > 0);
    }
    return (item.sensors || []).length > 0;
  });

  const isLayerDisabled = (key: string) => {
    switch (key) {
      case 'satellites':
      case 'trails':
      case 'groundTracks':
      case 'los':
        return !hasSats;
      case 'sensorFovs':
        return !hasSats || !hasAnySensor;
      case 'ground':
      case 'gsFov':
        return !hasGS;
      case 'geofeatures':
        return !hasGeo;
      case 'labels':
        return !hasAnyObject;
      default:
        return false;
    }
  };

  // Derive the displayed map-view from viewMode (split shows 3D on right)
  const mapView = viewMode === '2D' ? '2D' : '3D';

  const [pos, setPos] = useState<{ bottom: number; right: number }>({ bottom: 90, right: 60 });

  // Anchor panel to the bottom start of RightTools
  useLayoutEffect(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 8;
    const timelineOffset = timelineCollapsed ? 82 : timelineHeight + 52;
    setPos({
      right: window.innerWidth - rect.left + gap,
      bottom: timelineOffset,
    });
  }, [btnRef, timelineCollapsed, timelineHeight]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (btnRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('click', handleOutside);
    return () => document.removeEventListener('click', handleOutside);
  }, [btnRef, onClose]);

  const handleMapView = (v: '2D' | '3D') => {
    if (viewMode === 'split') {
      showToastMessage('info', 'Split view closed');
    }
    setViewMode(v);
    cesium.setSceneMode(v === '2D' ? 'SCENE2D' : 'SCENE3D');
  };

  const handleToggle = (key: string) => {
    if (!layers) return;
    const updated = { ...layers, [key]: !layers[key as keyof typeof layers] };
    if (key === 'los') {
      updated.losSatGS = updated.los;
    }
    toggleLayer(key);
    cesium.updateLayers(updated);
  };

  const isOn = (key: string) => !!layers?.[key as keyof typeof layers];

  const CheckRow = ({ layerKey, label }: { layerKey: string; label: string }) => {
    const on = isOn(layerKey);
    const disabled = isLayerDisabled(layerKey);
    return (
      <button
        type="button"
        disabled={disabled}
        className={`${styles.layersRow} ${on ? styles.on : ''} ${disabled ? styles.disabled : ''}`}
        onMouseDown={e => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle(layerKey);
        }}
      >
        <span className={styles.left}>
          <span className={styles.check}>
            {on && <CheckIcon />}
          </span>
          {label}
        </span>
      </button>
    );
  };

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      style={{ bottom: pos.bottom, right: pos.right }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      {/* ── Header ── */}
      <div className={styles.layersHd}>
        <div className={styles.layersHdL}>
          <LayersIcon />
          LAYERS
        </div>
        <div className={styles.layersHdR}>
          <CustomToolTip title="Close">
            <button className={styles.modalIconBtn} type="button" onClick={onClose}>
              <CloseIcon />
            </button>
          </CustomToolTip>
        </div>
      </div>

      {/* ── Body ── */}
      <div className={styles.layersBody}>
        {/* Map View — 2D/3D segment */}
        <div className={styles.layersRow} style={{ cursor: 'default' }}>
          <span className={styles.left}>Map View</span>
          <span className={styles.layersSegment}>
            <button
              type="button"
              className={mapView === '2D' ? styles.segActive : ''}
              onMouseDown={e => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                handleMapView('2D');
              }}
            >
              2D
            </button>
            <button
              type="button"
              className={mapView === '3D' ? styles.segActive : ''}
              onMouseDown={e => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                handleMapView('3D');
              }}
            >
              3D
            </button>
          </span>
        </div>

        {/* Objects section */}
        <div className={styles.layersSectionLbl}>Objects</div>
        {OBJECT_LAYERS.map(({ key, label }) => (
          <CheckRow key={key} layerKey={key} label={label} />
        ))}

        {/* Visualizations section */}
        <div className={styles.layersSectionLbl}>Visualizations</div>
        {VIZ_LAYERS.map(({ key, label }) => (
          <CheckRow key={key} layerKey={key} label={label} />
        ))}

        <div className={styles.layersSectionLbl}>Map Style</div>
        <div className={styles.styleGrid}>
          {MAP_STYLE_OPTIONS.map(option => (
            <CustomToolTip key={option.value} title={option.label}>
              <button
                type="button"
                className={`${styles.styleOption} ${option.value === cesium.mapTheme ? styles.styleOptionActive : ''}`}
                onMouseDown={e => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  void cesium.setMapTheme(option.value);
                }}
              >
                <span className={`${styles.stylePreview} ${styles[option.tone]}`} aria-hidden="true" />
                <span>{option.label}</span>
              </button>
            </CustomToolTip>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LayersPanel;
